// appFetch — `fetch` for the app's own raw data calls, with one difference:
// on the ANDROID app it goes through Android's native HTTP stack
// (CapacitorHttp → HttpURLConnection) instead of the WebView's.
//
// Why: after the Android app sits idle, saves timed out and needed many
// retries. Supabase's edge logs showed the WebView talking HTTP/3 (QUIC) and
// replies going missing — a booking POST answered 200 in ~0.2s server-side
// while the app still timed out, warmed a "fresh socket" and retried. Inside
// a WebView, JavaScript cannot force a new connection: the warm-up and the
// retry ride the same pooled, dead session, so retrying doesn't heal it.
// The native stack has no QUIC, enforces real socket timeouts, and opens a
// clean connection per request. It also skips the CORS preflight the
// WebView sends before every cross-origin call.
//
// Scope, deliberately narrow:
//   • Android only. iOS and the web keep `window.fetch` unchanged.
//   • JSON / text request bodies only — never Blob/FormData (uploads stay on
//     the WebView; that is what breaks CapacitorHttp's global fetch patch).
//   • Returns a small Response-like object: ok, status, headers.get(),
//     text(), json(). That is all the call sites use.
// If the native plugin is unavailable, it falls back to `window.fetch`.

import { CapacitorHttp } from "@capacitor/core";

const isAndroidApp = () => {
  try {
    const C = typeof window !== "undefined" ? window.Capacitor : null;
    return !!(C?.isNativePlatform?.() && C.getPlatform?.() === "android" && C.isPluginAvailable?.("CapacitorHttp"));
  } catch { return false; }
};

const headersOf = (h) => {
  const map = {};
  for (const [k, v] of Object.entries(h || {})) if (k) map[k.toLowerCase()] = String(v);
  return { get: (name) => map[String(name).toLowerCase()] ?? null };
};

// The native side PARSES a JSON reply before handing it back (an object, an
// array, a number, or a JSON string literal with its quotes stripped) — but
// only when the Content-Type contains "application/json" (plus
// "application/vnd.api+json" on an error reply). Everything else, including
// PostgREST's "application/vnd.pgrst.object+json", arrives as raw text.
// Mirror that rule exactly and turn a parsed reply back into the text the
// server sent, so callers can keep calling text()/json().
const bodyText = (data, contentType, status) => {
  const parsed = contentType.includes("application/json")
    || (status >= 400 && contentType.includes("application/vnd.api+json"));
  if (!parsed) return data == null ? "" : String(data);
  if (data == null) return "null";
  if (typeof data === "string") return data === "" ? "" : JSON.stringify(data); // empty body / string literal
  return JSON.stringify(data);
};

export async function appFetch(url, init = {}) {
  if (!isAndroidApp() || (init.body != null && typeof init.body !== "string")) {
    return fetch(url, init);
  }
  const method = (init.method || "GET").toUpperCase();
  const timeoutMs = init.timeoutMs || 12000;
  let r;
  try {
    r = await CapacitorHttp.request({
      url,
      method,
      headers: init.headers || {},
      // A string body goes out byte-for-byte (the plugin writes it raw for a
      // JSON Content-Type); GET/HEAD never carry one.
      data: method === "GET" || method === "HEAD" ? undefined : (init.body ?? undefined),
      connectTimeout: Math.min(timeoutMs, 10000),
      readTimeout: timeoutMs,
      responseType: "text",
    });
  } catch (e) {
    // Normalise so callers' existing handling applies: a socket timeout becomes
    // the same `net-timeout` their retry logic already understands.
    const msg = String(e?.message || e || "");
    const err = new Error("appFetch " + method + " failed: " + msg.slice(0, 160));
    if (/timeout|timed out/i.test(msg)) err.code = "net-timeout";
    throw err;
  }
  const headers = headersOf(r.headers);
  const text = bodyText(r.data, (headers.get("content-type") || "").toLowerCase(), r.status);
  return {
    ok: r.status >= 200 && r.status < 300,
    status: r.status,
    headers,
    text: async () => text,
    json: async () => JSON.parse(text),
    native: true,
  };
}

export const usesNativeHttp = isAndroidApp;
